import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface ComboboxProps<T> {
  items: T[]
  value?: string
  onValueChange: (value: string) => void
  getLabel: (item: T) => string
  getValue: (item: T) => string
  getDescription?: (item: T) => string
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
}

export function Combobox<T>({
  items,
  value,
  onValueChange,
  getLabel,
  getValue,
  getDescription,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No items found",
}: ComboboxProps<T>) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const filtered = items.filter((item) =>
    getLabel(item).toLowerCase().includes(search.toLowerCase())
  )

  const selectedItem = items.find((item) => getValue(item) === value)

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between"
        onClick={() => setOpen(!open)}
      >
        {selectedItem ? getLabel(selectedItem) : placeholder}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
          <div className="p-2 border-b">
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500">
                {emptyText}
              </div>
            ) : (
              filtered.map((item) => {
                const itemValue = getValue(item)
                const isSelected = value === itemValue
                return (
                  <div
                    key={itemValue}
                    className={cn(
                      "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100",
                      isSelected && "bg-gray-100"
                    )}
                    onClick={() => {
                      onValueChange(itemValue)
                      setOpen(false)
                      setSearch("")
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex-1">
                      <div>{getLabel(item)}</div>
                      {getDescription && (
                        <div className="text-xs text-gray-500">
                          {getDescription(item)}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
      
      {open && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setOpen(false)
            setSearch("")
          }}
        />
      )}
    </div>
  )
}
